<template>
  <default>
    <div
      v-if="appVersion"
      class="mainWrap ps-3 ps-md-0 mt-5 navtab-border-left"
    >
      <div class="row">
        <div class="col-md-2 col-sm-4 d-flex align-items-top">
          <div class="nav-menu-container">
            <ul
              class="nav-menu-app-edit nav flex-column nav-pills"
              role="tablist"
            >
              <li v-for="(option, index) in options" :key="index">
                <StoreLink
                  class="nav-link"
                  :to="option.link"
                  :class="{ active: isActive(option.link) }"
                >
                  {{ option.name }}
                </StoreLink>
              </li>
            </ul>
          </div>
        </div>
        <div class="col-md-6 col-sm-8 d-flex align-items-center">
          <h1 class="break-all">
            Edit: <br class="d-block d-lg-none" />
            <StoreLink
              class="break-all"
              :href="`/${userHub?.hub?.name}/apps/${appVersion?.id}?dev-mode=true`"
              >{{ appVersion?.appSettings?.name }}
            </StoreLink>
          </h1>
        </div>
        <!-- app has icon -->
        <div
          v-if="appVersion?.appSettings?.iconUrl"
          class="col-lg-2 d-none d-xl-flex align-items-center"
        >
          <StoreButton :href="softwareProductUrl"
            >Back To Software Products
          </StoreButton>
        </div>
        <div
          v-if="appVersion?.appSettings?.iconUrl"
          class="col-md-2 d-xl-none col-sm-6 d-flex align-items-center"
        >
          <StoreButton :href="softwareProductUrl">Back</StoreButton>
        </div>
        <div
          v-if="appVersion?.appSettings?.iconUrl"
          class="col-md-2 col-sm-6 d-flex justify-content-end flex-shrink-0 align-items-center"
        >
          <div class="app-image-wrapper">
            <img
              alt="app icon"
              :src="appVersion?.appSettings?.iconUrl"
              class="img-thumbnail app-detail-img"
            />
          </div>
        </div>
        <!-- app has icon end -->

        <!-- app without icon -->
        <div
          v-if="!appVersion?.appSettings?.iconUrl"
          class="col-md-4 col-sm-12 d-flex align-items-center"
        >
          <StoreButton :href="softwareProductUrl"
            >Back to Software Products
          </StoreButton>
        </div>
        <!-- app without icon end -->
      </div>
      <div class="row">
        <div class="col-12">
          <hr />
        </div>
      </div>
      <slot />
    </div>
  </default>
</template>

<script lang="ts" setup>
import Default from "~/layouts/default.vue";
import { useRoute } from "vue-router";
import StoreLink from "@/components/shared/buttons/StoreLink.vue";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import { useLoadingSpinnerStore } from "~/stores/ui/loadingspinnerstore";
import { useAppVersionsStore } from "~/stores/appversionsstore";

const route = useRoute();

function isActive(link: string): boolean {
  return link === route.path;
}

const userId = route.params.userid as string;

const userHub = computed(() => {
  return useUserStore().userHub;
});

const appVersionId = computed(() => {
  return parseInt(route.params.appversionid as string, 10);
});
const appVersionsStore = useAppVersionsStore();

watch(
  appVersionId,
  async () => {
    useLoadingSpinnerStore().start();
    try {
      await appVersionsStore.getOrLoadAppVersionById(appVersionId.value);
    } finally {
      useLoadingSpinnerStore().stop();
    }
  },
  { immediate: true },
);

const appVersion = computed(() =>
  appVersionsStore.getCachedAppVersionById(appVersionId.value),
);

const softwareProductUrl = `/users/${userId}/developer/software-products`;
const baseLink = computed(() => {
  return `${softwareProductUrl}/edit-app/${appVersionId.value}`;
});
const options = computed(() => {
  return [
    { name: "Core Data", link: `${baseLink.value}/coredata` },
    { name: "Pricing", link: `${baseLink.value}/licenseoptions` },
    { name: "Developers", link: `${baseLink.value}/developers` },
    { name: "Version History", link: `${baseLink.value}/history` },
    { name: "Version Data", link: `${baseLink.value}/versiondata` },
  ];
});
</script>
<style lang="scss" scoped>
@import "@/sass/variables.scss";

.nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  background-color: $phoenix-cyan;
}

.nav-link {
  font-weight: bold;
  font-size: 0.9rem;
  border-radius: 0px;
  border-bottom: 1px solid $phoenix-cyan;
  padding-left: 0.25rem;
  padding-right: 1rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  cursor: pointer;
  color: inherit;

  &:hover {
    color: $phoenix-cyan;
  }
}

.nav-menu-app-edit {
  border-top: 1px solid $phoenix-cyan;
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-menu-container {
  display: flex;
  flex-direction: column;
}

.navtab-border-left {
  padding-left: 0px;
  box-shadow: -20px 0px 0px 0px $phoenix-cyan;
}

hr {
  border: 1px solid $phoenix-cyan;
}

.app-image-wrapper {
  width: 180px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
}

img.img-thumbnail.app-detail-img {
  border-radius: 18px;
  width: 180px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain;
  flex-shrink: 0;
}

.break-all {
  word-wrap: break-word;
  word-break: break-all;
}
</style>
